import { Lock } from "lucide-react";
import { cn } from "@/lib/utils";

interface SecurePaymentBadgeProps {
  className?: string;
  variant?: "default" | "compact";
}

export function SecurePaymentBadge({ className, variant = "default" }: SecurePaymentBadgeProps) {
  return (
    <div className={cn(
      "flex flex-col items-center gap-3",
      variant === "compact" && "gap-2",
      className
    )}>
      <div className="flex items-center gap-2 text-muted-foreground">
        <Lock className={cn("text-primary", variant === "compact" ? "h-4 w-4" : "h-5 w-5")} />
        <span className={cn("font-medium", variant === "compact" ? "text-xs" : "text-sm")}>
          Secure payments
        </span>
      </div>
      
      <div className={cn(
        "flex items-center justify-center gap-4 flex-wrap",
        variant === "compact" && "gap-3"
      )}>
        {/* Paystack Icon Only */}
        <div className="flex items-center gap-1.5 opacity-70 hover:opacity-100 transition-opacity">
          <svg
            className={cn(variant === "compact" ? "h-5 w-5" : "h-6 w-6")}
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <rect width="24" height="24" rx="4" fill="#00C3F7" />
            <rect x="6" y="6" width="12" height="2.5" rx="1" fill="white" />
            <rect x="6" y="10.5" width="8" height="2.5" rx="1" fill="white" />
            <rect x="6" y="15" width="12" height="2.5" rx="1" fill="white" />
          </svg>
          <span className={cn("font-semibold text-[#00C3F7]", variant === "compact" ? "text-xs" : "text-sm")}>
            Paystack
          </span>
        </div>

        {/* Visa Logo */}
        <svg
          className={cn("opacity-70 hover:opacity-100 transition-opacity", variant === "compact" ? "h-4" : "h-5")}
          viewBox="0 0 48 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M19.5 0.5L16 15.5H12.5L16 0.5H19.5Z"
            fill="#1A1F71"
          />
          <path
            d="M33.5 0.8C32.7 0.5 31.4 0.1 29.8 0.1C25.3 0.1 22.1 2.5 22.1 5.8C22.1 8.3 24.3 9.6 26 10.5C27.7 11.4 28.3 12 28.3 12.8C28.3 14 26.9 14.5 25.6 14.5C23.8 14.5 22.8 14.2 21.3 13.6L20.7 13.3L20 17.1C21.2 17.6 23.2 18.1 25.3 18.1C30.1 18.1 33.2 15.7 33.2 12.2C33.2 10.2 32 8.7 29.4 7.5C27.8 6.7 26.9 6.1 26.9 5.3C26.9 4.6 27.7 3.8 29.4 3.8C30.8 3.8 31.9 4.1 32.7 4.4L33.1 4.6L33.5 0.8Z"
            fill="#1A1F71"
          />
          <path
            d="M38.7 0.5C37.8 0.5 37.1 0.8 36.7 1.7L29.8 15.5H34.5L35.4 13H41.2L41.7 15.5H46L42.3 0.5H38.7ZM36.6 9.9C36.9 9.1 38.4 5.2 38.4 5.2C38.4 5.2 38.8 4.1 39 3.4L39.4 5L40.3 9.9H36.6Z"
            fill="#1A1F71"
          />
          <path
            d="M12.1 0.5L7.6 10.8L7.1 8.3C6.3 5.5 3.7 2.5 0.7 1L4.8 15.5H9.6L17 0.5H12.1Z"
            fill="#1A1F71"
          />
          <path
            d="M5.5 0.5H0.1L0 0.8C5.5 2.2 9.2 5.5 10.6 9.4L9.1 1.7C8.9 0.9 8.2 0.6 7.4 0.5H5.5Z"
            fill="#F9A533"
          />
        </svg>

        {/* Mastercard Logo */}
        <svg
          className={cn("opacity-70 hover:opacity-100 transition-opacity", variant === "compact" ? "h-5" : "h-6")}
          viewBox="0 0 32 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <circle cx="10" cy="10" r="10" fill="#EB001B" />
          <circle cx="22" cy="10" r="10" fill="#F79E1B" />
          <path
            d="M16 3.5C17.9 5 19.1 7.3 19.1 10C19.1 12.7 17.9 15 16 16.5C14.1 15 12.9 12.7 12.9 10C12.9 7.3 14.1 5 16 3.5Z"
            fill="#FF5F00"
          />
        </svg>

        {/* Verve Logo - Icon + Text */}
        <div className="flex items-center gap-1 opacity-70 hover:opacity-100 transition-opacity">
          <svg
            className={cn(variant === "compact" ? "h-5 w-5" : "h-6 w-6")}
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <rect width="24" height="24" rx="4" fill="#00425F" />
            <circle cx="18" cy="6" r="4" fill="#ED1C24" />
            <path d="M6 8L9 16L12 8" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          <span className={cn("font-semibold", variant === "compact" ? "text-xs" : "text-sm")}>
            <span className="text-[#00425F]">Verv</span>
            <span className="text-[#ED1C24]">e</span>
          </span>
        </div>
      </div>
    </div>
  );
}
